import { Text, Flex, Menu, MenuList, MenuItem, MenuButton, Icon, Button } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import IconDown from '@/assets/icons/ic-chevron-down.svg';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { useLecturePlayerContext } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.context';
import { usePlayerState } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.hooks';
import { setActiveTrack, setHideTrack } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.utils';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { Transcript } from '@/resources/Transcript';

import { sortTranscriptsByLanguagePosition } from './LectureLanguageDropdown.utils';

export interface ILectureLanguageDropdownProps {
  label?: string;
}

export const LectureLanguageDropdown: FC<ILectureLanguageDropdownProps> = ({ label }) => {
  const { t } = useTranslation();
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lectureId));
  const [player] = useLecturePlayerContext();
  const { activeTrack } = usePlayerState();
  const activeTrackId = activeTrack?.id;

  const handleSetTrack = (transcript: Transcript) => {
    setActiveTrack(player, transcript.id);
  };

  const handleHideTrack = () => {
    setHideTrack(player);
  };

  const selectedTranscript = useMemo(
    () => activeTrackId && transcripts.find((transcript) => transcript.id === activeTrackId),
    [transcripts, activeTrackId]
  );

  // sort transcript by language position
  const sortedTranscripts = useMemo(() => sortTranscriptsByLanguagePosition(transcripts), [transcripts]);

  // dont show if no transcripts are available
  if (!transcripts.length) {
    return null;
  }

  return (
    <Menu variant="navigation" colorScheme="blackAlpha" placement="bottom-end">
      <MenuButton
        as={Button}
        rightIcon={<Icon as={IconDown} w="24px" h="24px" />}
        bg="dropdownAlpha"
        color="white"
        backdropFilter="blur(75px)"
        borderRadius="base"
        h={{ base: '40px', md: '50px' }}
        px="4"
        _hover={{
          bg: 'dropdownAlpha',
        }}
        _active={{
          bg: 'dropdownAlpha',
        }}
      >
        <Flex alignItems="center">
          {label && (
            <Text fontFamily="heading" fontSize="sm" lineHeight="1.125rem" fontWeight="bold" mr={4}>
              {label}
            </Text>
          )}
          <Text fontSize="sm" fontWeight="normal" lineHeight="1.25rem">
            {selectedTranscript?.language.name || t('common.disabled')}
          </Text>
        </Flex>
      </MenuButton>
      <MenuList>
        {activeTrackId && <MenuItem onClick={handleHideTrack}>{t('common.disabled')}</MenuItem>}
        {sortedTranscripts?.map((transcript) => (
          <MenuItem
            key={transcript.id}
            onClick={() => handleSetTrack(transcript)}
            isDisabled={activeTrackId === transcript.id}
          >
            {transcript.language.name}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
